import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Wallet, Search, Loader2 } from "lucide-react";
import { API } from "@/lib/api";

/**
 * AdminUserFix — one-stop admin utility for real-world data cleanup.
 *  Lookup by email → see wallet balance + recent bookings → adjust wallet
 *  in one form. Also lists recent bookings inline so the admin can eyeball
 *  which trips need a payment-method reassignment (e.g. rides that ran
 *  during a Stripe outage and should be re-tagged as Cash / Stripe Tap).
 *  Reassignment itself uses the existing PATCH /admin/bookings/{id}
 *  endpoint via a dropdown per row.
 */
export default function AdminUserFix() {
  const [email, setEmail] = useState("");
  const [lookup, setLookup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [delta, setDelta] = useState("");   // dollars, positive or negative
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [rowSaving, setRowSaving] = useState(null);

  const doLookup = async () => {
    if (!email.trim()) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/users/lookup`, { params: { email: email.trim() } });
      setLookup(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lookup failed");
      setLookup(null);
    } finally {
      setLoading(false);
    }
  };

  const doAdjust = async () => {
    const dollars = parseFloat(delta);
    if (!Number.isFinite(dollars) || dollars === 0) {
      return toast.error("Enter a dollar amount (positive to add, negative to remove).");
    }
    setSaving(true);
    try {
      const r = await axios.post(
        `${API}/admin/users/wallet-adjust`,
        { delta_cents: Math.round(dollars * 100), note: note.trim() },
        { params: { email: lookup.user.email } },
      );
      toast.success(`Balance now $${(r.data.new_balance_cents / 100).toFixed(2)}`);
      setDelta("");
      setNote("");
      await doLookup();  // refresh
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Adjustment failed");
    } finally {
      setSaving(false);
    }
  };

  const reassignPayment = async (bookingId, payment_status) => {
    // payment_status → payment_method mapping (matches backend advance logic)
    const payment_method = payment_status === "paid"
      ? "stripe"
      : payment_status === "stripe_tap"
      ? "stripe_tap"
      : payment_status; // cash
    setRowSaving(bookingId);
    try {
      await axios.patch(`${API}/admin/bookings/${bookingId}`, {
        payment_status,
        payment_method,
      });
      toast.success("Payment method updated");
      await doLookup();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setRowSaving(null);
    }
  };

  const balanceCents = lookup?.user?.wallet_credits_cents || 0;

  return (
    <Card className="bg-[#121212] border-white/5" data-testid="admin-user-fix">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-medium text-white flex items-center gap-2">
          <Wallet className="w-4 h-4 text-[#4D88FF]" />
          Customer fix (wallet + booking payment method)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Input
            placeholder="customer@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && doLookup()}
            className="bg-black border-white/10 text-sm"
            data-testid="admin-fix-email"
          />
          <Button
            onClick={doLookup}
            disabled={loading || !email.trim()}
            className="bg-[#0047FF] hover:bg-[#003BE0]"
            data-testid="admin-fix-lookup"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </Button>
        </div>

        {lookup && (
          <div className="space-y-3">
            <div className="rounded-lg border border-white/5 bg-white/[0.02] p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-zinc-500 font-bold">{lookup.user.name || "—"}</p>
                  <p className="text-sm text-white">{lookup.user.email}</p>
                  <p className="text-[10px] text-zinc-500 mt-0.5">
                    role: {lookup.user.role || "—"} · tenant: {lookup.user.tenant_id || "—"}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs uppercase tracking-wider text-zinc-500 font-bold">Wallet</p>
                  <p className="text-xl font-black text-emerald-300" data-testid="admin-fix-balance">
                    ${(balanceCents / 100).toFixed(2)}
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-white/5 bg-white/[0.02] p-3 space-y-2">
              <p className="text-xs uppercase tracking-wider text-zinc-500 font-bold">Adjust balance</p>
              <div className="flex gap-2">
                <Input
                  type="number"
                  step="0.01"
                  placeholder="dollars (use minus for a debit)"
                  value={delta}
                  onChange={(e) => setDelta(e.target.value)}
                  className="bg-black border-white/10 text-sm"
                  data-testid="admin-fix-delta"
                />
                <Button
                  onClick={doAdjust}
                  disabled={saving || !delta}
                  className="bg-emerald-600 hover:bg-emerald-500"
                  data-testid="admin-fix-apply"
                >
                  {saving ? "Saving…" : "Apply"}
                </Button>
              </div>
              <Textarea
                rows={2}
                placeholder="Note (why is this being adjusted?)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="bg-black border-white/10 text-xs"
                data-testid="admin-fix-note"
              />
            </div>

            <div className="rounded-lg border border-white/5 bg-white/[0.02] p-3 space-y-1.5">
              <p className="text-xs uppercase tracking-wider text-zinc-500 font-bold">
                Recent bookings ({lookup.recent_bookings.length})
              </p>
              {lookup.recent_bookings.length === 0 && (
                <p className="text-xs text-zinc-500 py-2">No bookings on file.</p>
              )}
              {lookup.recent_bookings.map((b) => (
                <div
                  key={b.id}
                  className="flex items-center justify-between gap-2 py-1.5 border-b border-white/5 last:border-0"
                  data-testid={`admin-fix-booking-${b.id}`}
                >
                  <div className="text-[11px] min-w-0 flex-1">
                    <p className="text-white truncate">
                      {b.scheduled_date} {b.scheduled_time} · ${Number(b.estimated_price || 0).toFixed(2)}
                    </p>
                    <p className="text-zinc-500">
                      {b.status} · {b.payment_method || "—"} · {b.payment_status || "—"}
                    </p>
                  </div>
                  <select
                    value={b.payment_status || ""}
                    disabled={rowSaving === b.id}
                    onChange={(e) => reassignPayment(b.id, e.target.value)}
                    className="text-[11px] bg-black border border-white/10 rounded px-1.5 py-1 text-white"
                    data-testid={`admin-fix-reassign-${b.id}`}
                  >
                    <option value="">Set method…</option>
                    <option value="paid">Stripe</option>
                    <option value="stripe_tap">Stripe Tap</option>
                    <option value="cash">Cash</option>
                    <option value="unpaid">Unpaid</option>
                  </select>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
